/**
 * WebSocket client with auto-reconnect.
 *
 * Usage:
 *
 *   const ws = openWs(lobbyId, {
 *     onMessage: (msg) => ...,
 *     onConnect: () => ...,
 *     onClose: () => ...,
 *   });
 *   ws.send({ t: "take_seat", seatIndex: 0 });
 *   ws.close();
 */

import type { ClientMsg, ServerMsg } from "../../../shared/protocol.js";

export interface WsHandle {
  send: (msg: ClientMsg) => void;
  close: () => void;
  readonly isOpen: boolean;
}

interface WsHandlers {
  onMessage: (msg: ServerMsg) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export function openWs(lobbyId: string, handlers: WsHandlers): WsHandle {
  let ws: WebSocket | null = null;
  let manuallyClosed = false;
  let reconnectAttempt = 0;
  let pendingSends: ClientMsg[] = [];

  function connect() {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    ws = new WebSocket(`${proto}//${location.host}/ws`);
    ws.addEventListener("open", () => {
      reconnectAttempt = 0;
      ws!.send(JSON.stringify({ t: "hello", lobbyId } satisfies ClientMsg));
      for (const m of pendingSends) ws!.send(JSON.stringify(m));
      pendingSends = [];
      handlers.onOpen?.();
    });
    ws.addEventListener("message", (e) => {
      try {
        const msg = JSON.parse(e.data) as ServerMsg;
        handlers.onMessage(msg);
      } catch {
        // ignore
      }
    });
    ws.addEventListener("close", () => {
      handlers.onClose?.();
      if (manuallyClosed) return;
      reconnectAttempt++;
      const delay = Math.min(8000, 500 * 2 ** reconnectAttempt);
      setTimeout(connect, delay);
    });
    ws.addEventListener("error", () => {
      // Close handler will fire; nothing to do here.
    });
  }

  connect();

  return {
    send(msg: ClientMsg) {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      } else {
        pendingSends.push(msg);
      }
    },
    close() {
      manuallyClosed = true;
      ws?.close();
    },
    get isOpen() {
      return ws?.readyState === WebSocket.OPEN;
    },
  };
}
